import React from 'react';
import { Helmet } from 'react-helmet-async';

// SEO Configuration for each page
export const seoConfig = {
  home: {
    title: "APD NEXUS: Florida Disability Services Guide | iBudget & Waiver Info",
    description: "Official resource platform for Florida's Agency for Persons with Disabilities (APD). Access iBudget waiver services, eligibility requirements, and support for 36,000+ individuals with developmental disabilities.",
    canonical: "https://apdnexus.com/",
    keywords: "APD Florida, Agency for Persons with Disabilities, iBudget Florida, developmental disabilities Florida"
  },
  eligibility: {
    title: "APD Florida Eligibility 2026 | Requirements & Application Process",
    description: "Complete guide to APD eligibility in Florida. Learn about the 7 qualifying developmental disabilities, required documentation, application process, and processing timelines.",
    canonical: "https://apdnexus.com/eligibility",
    keywords: "APD eligibility Florida, APD application, developmental disability eligibility, how to apply APD"
  },
  waiverPrograms: {
    title: "Florida Waiver Programs | iBudget, CDC+, & Waitlist Information",
    description: "Comprehensive overview of Florida APD waiver programs including iBudget Florida, CDC+ consumer-directed care, HB 1103, and the pre-enrollment waitlist system.",
    canonical: "https://apdnexus.com/waiver-programs",
    keywords: "iBudget Florida, CDC+ Florida, APD waiver, Medicaid waiver Florida, developmental disabilities waiver"
  },
  ibudget: {
    title: "iBudget Florida Waiver Guide | Services, Rates & Algorithm",
    description: "Complete guide to iBudget Florida - the $2.2 billion Medicaid waiver program serving 36,000+ individuals. Learn about QSI assessments, budget allocation, and available services.",
    canonical: "https://apdnexus.com/waiver-programs/ibudget",
    keywords: "iBudget Florida, iBudget waiver, QSI assessment, APD budget allocation"
  },
  cdcPlus: {
    title: "CDC+ Florida Program Guide | Consumer Directed Care Plus",
    description: "Consumer Directed Care Plus (CDC+) program guide. Learn how to trade 8% of your budget for enhanced flexibility, hire family members, and manage your own care.",
    canonical: "https://apdnexus.com/waiver-programs/cdc-plus",
    keywords: "CDC+ Florida, consumer directed care, self-directed care Florida, APD CDC+"
  },
  waitlist: {
    title: "APD Waitlist Florida 2026 | Priority Categories & Current Status",
    description: "Current APD pre-enrollment waitlist information. 17,393 individuals waiting as of December 2025. Learn about the 7 priority categories and enrollment process.",
    canonical: "https://apdnexus.com/waiver-programs/waitlist",
    keywords: "APD waitlist Florida, APD waiting list, pre-enrollment waitlist, APD priority categories"
  },
  regionalOffices: {
    title: "APD Regional Offices Florida | Find Your Local APD Office",
    description: "Find your APD regional office. 6 regions serving all 67 Florida counties - Northwest, Northeast, Central, Suncoast, Southeast, and Southern regions.",
    canonical: "https://apdnexus.com/regional-offices",
    keywords: "APD regional offices, APD Florida locations, APD office near me, Florida APD regions"
  },
  services: {
    title: "APD Services Catalog | Rates, Limits & Service Families",
    description: "Complete APD services catalog with 2024-25 rates. Life skills, personal supports, residential habilitation, behavior analysis, therapy services, and more.",
    canonical: "https://apdnexus.com/services",
    keywords: "APD services Florida, iBudget services, residential habilitation, behavior analysis services"
  },
  providers: {
    title: "APD Provider Enrollment | 11-Step Process & Requirements",
    description: "Become an APD iBudget provider in Florida. Complete 11-step enrollment guide including background screening, Medicaid enrollment, and service authorization.",
    canonical: "https://apdnexus.com/providers",
    keywords: "APD provider enrollment, become APD provider, iBudget provider Florida, APD background screening"
  },
  consumerRights: {
    title: "APD Consumer Rights | Bill of Rights & Fair Hearing Process",
    description: "Know your rights under Florida APD. Bill of Rights (§393.13), fair hearing procedures, guardianship alternatives, and advocacy organization resources.",
    canonical: "https://apdnexus.com/consumer-rights",
    keywords: "APD consumer rights, disability rights Florida, fair hearing APD, guardian advocacy Florida"
  },
  legalFramework: {
    title: "APD Legal Framework | Chapter 393 & Administrative Code 65G",
    description: "Legal foundation for Florida APD services. Chapter 393 Florida Statutes, Administrative Code 65G, federal Medicaid compliance, and HCBS requirements.",
    canonical: "https://apdnexus.com/legal-framework",
    keywords: "Chapter 393 Florida, APD statutes, 65G administrative code, HCBS Florida"
  },
  contacts: {
    title: "APD Contact Information | CARES Line, Abuse Hotline & Resources",
    description: "Essential APD Florida contact information. APD CARES Line 1-866-273-2273, Florida Abuse Hotline, regional offices, and advocacy organizations.",
    canonical: "https://apdnexus.com/contacts",
    keywords: "APD CARES Line, APD phone number, Florida Abuse Hotline, APD contact"
  }
};

// Breadcrumb configurations
const breadcrumbs = {
  home: [{ name: "Home", url: "https://apdnexus.com/" }],
  eligibility: [
    { name: "Home", url: "https://apdnexus.com/" },
    { name: "Eligibility", url: "https://apdnexus.com/eligibility" }
  ],
  waiverPrograms: [
    { name: "Home", url: "https://apdnexus.com/" },
    { name: "Waiver Programs", url: "https://apdnexus.com/waiver-programs" }
  ],
  ibudget: [
    { name: "Home", url: "https://apdnexus.com/" },
    { name: "Waiver Programs", url: "https://apdnexus.com/waiver-programs" },
    { name: "iBudget", url: "https://apdnexus.com/waiver-programs/ibudget" }
  ],
  cdcPlus: [
    { name: "Home", url: "https://apdnexus.com/" },
    { name: "Waiver Programs", url: "https://apdnexus.com/waiver-programs" },
    { name: "CDC+", url: "https://apdnexus.com/waiver-programs/cdc-plus" }
  ],
  waitlist: [
    { name: "Home", url: "https://apdnexus.com/" },
    { name: "Waiver Programs", url: "https://apdnexus.com/waiver-programs" },
    { name: "Waitlist", url: "https://apdnexus.com/waiver-programs/waitlist" }
  ],
  regionalOffices: [
    { name: "Home", url: "https://apdnexus.com/" },
    { name: "Regional Offices", url: "https://apdnexus.com/regional-offices" }
  ],
  services: [
    { name: "Home", url: "https://apdnexus.com/" },
    { name: "Services", url: "https://apdnexus.com/services" }
  ],
  providers: [
    { name: "Home", url: "https://apdnexus.com/" },
    { name: "Providers", url: "https://apdnexus.com/providers" }
  ],
  consumerRights: [
    { name: "Home", url: "https://apdnexus.com/" },
    { name: "Consumer Rights", url: "https://apdnexus.com/consumer-rights" }
  ],
  legalFramework: [
    { name: "Home", url: "https://apdnexus.com/" },
    { name: "Legal Framework", url: "https://apdnexus.com/legal-framework" }
  ],
  contacts: [
    { name: "Home", url: "https://apdnexus.com/" },
    { name: "Contacts", url: "https://apdnexus.com/contacts" }
  ]
};

export default function SEO({ page = 'home' }) {
  const config = seoConfig[page] || seoConfig.home;
  const pageBreadcrumbs = breadcrumbs[page] || breadcrumbs.home;

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": pageBreadcrumbs.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": item.url
    }))
  };

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{config.title}</title>
      <meta name="title" content={config.title} />
      <meta name="description" content={config.description} />
      <meta name="keywords" content={config.keywords} />
      <link rel="canonical" href={config.canonical} />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={config.canonical} />
      <meta property="og:title" content={config.title} />
      <meta property="og:description" content={config.description} />

      {/* Twitter */}
      <meta name="twitter:url" content={config.canonical} />
      <meta name="twitter:title" content={config.title} />
      <meta name="twitter:description" content={config.description} />

      {/* Breadcrumb Schema */}
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbSchema)}
      </script>
    </Helmet>
  );
}
